/**
 * src/utils/modalNavigation.ts
 * Manages modal & drawer dismiss stack and mobile hardware back button navigation
 */

type DismissHandler = () => void;
const dismissStack: DismissHandler[] = [];

if (typeof window !== 'undefined') {
  // Use capture phase so overlay dismissal handles popstate first
  window.addEventListener('popstate', (e) => {
    if (dismissStack.length > 0) {
      const topHandler = dismissStack.pop();
      if (topHandler) {
        try {
          topHandler();
        } catch (err) {
          console.warn('Error dismissing modal on popstate', err);
        }
        // Stop event propagation so underlying tab back-handler doesn't fire simultaneously
        e.stopImmediatePropagation();
      }
    }
  }, true);
}

/**
 * Register a modal/sheet/drawer for hardware back button closing
 */
export function registerOverlayDismiss(onDismiss: () => void): () => void {
  dismissStack.push(onDismiss);

  // Push dummy history entry so Android/iOS back gesture doesn't exit the app
  try {
    window.history.pushState({ ckauditOverlay: true, id: Date.now() }, '');
  } catch {}

  return () => {
    const idx = dismissStack.lastIndexOf(onDismiss);
    if (idx !== -1) {
      dismissStack.splice(idx, 1);
      // If closed by UI click instead of hardware back, rewind history
      if (typeof window !== 'undefined' && window.history.state?.ckauditOverlay) {
        window.history.back();
      }
    }
  };
}

export function hasActiveOverlays(): boolean {
  return dismissStack.length > 0;
}
